// Imports
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { darken } from 'polished';
import styled, { css } from 'styled-components/macro';
import selfActions from '../../../redux/self/actions';
import { useAppDispatch, useAppSelector } from '../../../shareables/foundation/front-end/redux/hooks';
import helperFunctions from '../../../shareables/foundation/front-end/utils/helper-functions';
import keyValueStorage from '../../../shareables/foundation/front-end/utils/key-value-storage';
import { GeneratedThemeProps } from '../../templates/template-style';
import ScreenSharingIndicator from './screen-sharing-indicator';
import { baseLinkStyle, StyledLink } from '../shared-components/styled-hyperlinks';
import SignInAnchor from '../sign-in-anchor';
import MobileNavigation from './mobile-navigation';
import NotificationBox from './notification-box';
import UserBox from './user-box';
import useMemberCheck from '../../../utils/use-member-check';


// Styled components
export const TopBarContainer = styled.div<{ theme: GeneratedThemeProps }>`
	box-shadow: 0 0 0.625rem rgba(0, 0, 0, 0.3);
	background: linear-gradient(
		to bottom,
		${(props) => props.theme.baseColor},
		${(props) => darken(0.08, props.theme.baseColor)}
	);
	color: #fff;
	min-height: 1.875rem;
	
	font-size: 0.8125rem;
	line-height: 1.875rem;
	
	position: relative;
	
	${(props) =>
		props.theme.isPaper &&
		css`
			background: #fff;
			color: ${props.theme.baseColor};
		`}
`;

const SignIn = styled.div`
	max-width: 62.5rem;
	
	margin: 0 auto;
	text-align: right;
	
	a {
		${baseLinkStyle}
	}
	
	@media only screen and (max-width: 47.9375rem) {
		display: none;
	}
`;

const Wrapper = styled.div`
	max-width: 62.5rem;
	margin: 0 auto;
	display: grid;
	grid-template-columns: 1fr auto;
	align-items: center;
	width: 100%;
	
	@media only screen and (max-width: 47.9375rem) {
		display: none;
	}
`;

const Alert = styled.div`
	text-align: left;
	grid-column: 1;
	
	.svg-inline--fa {
		margin-right: 0.1875rem;
	}
`;

const AdminMode = styled.span`
	text-decoration: underline;
	cursor: pointer;
	
	&:not(:first-child) {
		margin-left: 0.5rem;
	}
`;


// Define the accepted props
interface Props {
	storybookIsUserDropdownOpen?: true;
	storybookIsNotificationDropdownOpen?: true;
	storybookSharingActive?: true;
}


// Function component
const TopBar: React.FC<Props> = ({
	storybookIsUserDropdownOpen,
	storybookIsNotificationDropdownOpen,
	storybookSharingActive,
}) => {
	// Use Redux functionality
	const dispatch = useAppDispatch();
	const user = useAppSelector((state) => state.common.user);
	const member = useAppSelector((state) => state.self.member);
	const adminView = useAppSelector((state) => state.self.adminView);
	const isAdmin = useMemberCheck({ type: 'ANY_PRIVILEGE' });
	
	
	// Return JSX
	return (
		<TopBarContainer>
			{!user.signedIn && (
				<SignIn>
					Please <SignInAnchor>sign in</SignInAnchor> or <StyledLink to='/account/register'>register</StyledLink>
				</SignIn>
			)}
			
			{user.signedIn && (
				<Wrapper>
					{member && (!member.account.approved || member.account.suspended) && (
						<Alert>
							<FontAwesomeIcon icon={faExclamationTriangle} />
							Account {member.account.suspended ? 'suspended' : 'pending review'}
						</Alert>
					)}
					
					{isAdmin && (
						<Alert>
							<ScreenSharingIndicator storybookSharingActive={storybookSharingActive} />
							
							{!adminView && (
								<AdminMode
									{...helperFunctions.buttonize(() => {
										keyValueStorage.removeItem('viewing_as_non_admin');
										dispatch(selfActions.setAdminView(true));
									})}
								>
									Re-enter admin mode
								</AdminMode>
							)}
						</Alert>
					)}
					
					<UserBox storybookIsUserDropdownOpen={storybookIsUserDropdownOpen} />
					
					{member?.account?.approved && (
						<NotificationBox storybookIsNotificationDropdownOpen={storybookIsNotificationDropdownOpen} />
					)}
				</Wrapper>
			)}
			
			<MobileNavigation />
		</TopBarContainer>
	);
};

export default TopBar;
